import { Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { HotToastService } from '@ngxpert/hot-toast';
import { IssueService } from '../../../services/issue.service';
import { VersionService } from '../../../services/version.service';
import { IssueCategory, IssueSubCategory } from '../../../models/issue';
import { IssueType } from '../../../models/enums/issueType.enum';
import { IssuePriority } from '../../../models/enums/issuePriority.enum';

@Component({
  selector: 'app-issue-create',
  imports: [
    CommonModule, ReactiveFormsModule,
    MatButtonModule, MatIconModule, MatFormFieldModule, MatInputModule, MatSelectModule
  ],
  template: `
    <div class="component-container">
      <div class="component-header">
        <h2>Report an issue</h2>
      </div>
      <section class="section-container nrg-border-radius">
        <div class="section-content">
          <form [formGroup]="form" (ngSubmit)="submit()">
            <div class="row">
              <mat-form-field appearance="fill" class="grow">
                <mat-label>Title</mat-label>
                <input matInput formControlName="title" maxlength="200" />
                @if (form.controls.title.touched && form.controls.title.invalid) {
                  <mat-error>A title is required</mat-error>
                }
              </mat-form-field>
            </div>

            <div class="row">
              <mat-form-field appearance="fill">
                <mat-label>Type</mat-label>
                <mat-select formControlName="type">
                  @for (t of types; track t) { <mat-option [value]="t">{{ t }}</mat-option> }
                </mat-select>
              </mat-form-field>
              <mat-form-field appearance="fill">
                <mat-label>Priority</mat-label>
                <mat-select formControlName="priority">
                  @for (p of priorities; track p) { <mat-option [value]="p">{{ p }}</mat-option> }
                </mat-select>
              </mat-form-field>
              <mat-form-field appearance="fill">
                <mat-label>Category</mat-label>
                <mat-select formControlName="categoryId">
                  @for (c of categories; track c.id) { <mat-option [value]="c.id">{{ c.name }}</mat-option> }
                </mat-select>
                @if (form.controls.categoryId.touched && form.controls.categoryId.invalid) {
                  <mat-error>Select a category</mat-error>
                }
              </mat-form-field>
              <mat-form-field appearance="fill">
                <mat-label>Sub-category</mat-label>
                <mat-select formControlName="subCategoryId" [disabled]="subCategories.length === 0">
                  <mat-option [value]="''">None</mat-option>
                  @for (s of subCategories; track s.id) { <mat-option [value]="s.id">{{ s.name }}</mat-option> }
                </mat-select>
              </mat-form-field>
            </div>

            <div class="row">
              <mat-form-field appearance="fill" class="grow">
                <mat-label>Description</mat-label>
                <textarea matInput formControlName="description" rows="6"></textarea>
                @if (form.controls.description.touched && form.controls.description.invalid) {
                  <mat-error>A description is required</mat-error>
                }
              </mat-form-field>
            </div>

            <div class="row attachment">
              <button type="button" mat-stroked-button (click)="fileInput.click()">
                <mat-icon>attach_file</mat-icon> Attach screenshot
              </button>
              <input #fileInput type="file" hidden (change)="onFileSelected($event)" />
              @if (selectedFile) { <span class="file">{{ selectedFile.name }}</span> }
            </div>

            <div class="actions">
              <button type="button" mat-button (click)="cancel()">Cancel</button>
              <button type="submit" mat-flat-button color="primary" [disabled]="saving">Submit</button>
            </div>
          </form>
        </div>
      </section>
    </div>
  `,
  styles: [`
    /* Themed card (matches director edit / the rest of the app) */
    .section-container {
      background-color: color-mix(in srgb, var(--mat-sys-primary), transparent 95%) !important;
      padding: 1rem;
      margin-bottom: 1rem;
    }
    .section-content { display: flex; flex-direction: column; }
    .row { display: flex; flex-wrap: wrap; gap: 12px; }
    .row mat-form-field { width: 220px; }
    .grow { flex: 1 1 100%; width: 100% !important; }
    .attachment { align-items: center; margin-top: 16px; }
    .file { font-size: 0.9em; color: rgba(0,0,0,0.6); }
    .actions { display: flex; justify-content: flex-end; gap: 8px; margin-top: 8px; }
  `]
})
export class IssueCreateComponent implements OnInit {
  private fb = inject(FormBuilder);
  private issueService = inject(IssueService);
  private versionService = inject(VersionService);
  private router = inject(Router);
  private toast = inject(HotToastService);

  categories: IssueCategory[] = [];
  subCategories: IssueSubCategory[] = [];
  types = Object.values(IssueType);
  priorities = Object.values(IssuePriority);
  selectedFile: File | null = null;
  saving = false;

  /** App + Server version captured at report time (resolved in ngOnInit). */
  private reportedVersion = this.versionService.appDisplay;

  form = this.fb.group({
    title: ['', [Validators.required, Validators.maxLength(200)]],
    description: ['', Validators.required],
    type: [IssueType.Bug as string, Validators.required],
    priority: [IssuePriority.Medium as string, Validators.required],
    categoryId: ['', Validators.required],
    subCategoryId: ['']
  });

  ngOnInit(): void {
    this.versionService.getCombinedVersion().subscribe(v => (this.reportedVersion = v));

    this.issueService.getCategories().subscribe({
      next: c => (this.categories = c),
      error: () => this.toast.error('Error loading categories')
    });

    this.form.controls.categoryId.valueChanges.subscribe(catId => {
      const cat = this.categories.find(c => c.id === catId);
      this.subCategories = cat?.subCategories ?? [];
      this.form.controls.subCategoryId.setValue('');
    });
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.selectedFile = input.files?.[0] ?? null;
  }

  submit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    this.saving = true;
    const v = this.form.getRawValue();
    this.issueService.create({
      title: v.title!,
      description: v.description!,
      type: v.type!,
      priority: v.priority!,
      categoryId: v.categoryId!,
      subCategoryId: v.subCategoryId || null,
      reportedInVersion: this.reportedVersion
    }).subscribe({
      next: issue => {
        if (this.selectedFile) {
          this.issueService.addAttachment(issue.id, this.selectedFile).subscribe({
            next: () => this.afterCreate(issue.id),
            error: () => {
              this.toast.warning('Issue created, but the attachment failed to upload');
              this.afterCreate(issue.id);
            }
          });
        } else {
          this.afterCreate(issue.id);
        }
      },
      error: () => {
        this.saving = false;
        this.toast.error('Error creating issue');
      }
    });
  }

  private afterCreate(id: string): void {
    this.saving = false;
    this.toast.success('Issue created');
    this.router.navigate(['/issues', id]);
  }

  cancel(): void {
    this.router.navigate(['/issues']);
  }
}
